import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Briefcase,
  Check,
  FileUp,
  Mail,
  Upload,
  User,
} from "lucide-react";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import Layout from "../../components/layout/Layout";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Progress } from "../../components/ui/progress";
import { useLanguage } from "../../context/LanguageContext";
import { adeverintaAPI } from "../../lib/api";

// Validation schema
const createSchema = (t) =>
  z.object({
    // Step 1: Personal Info
    nume: z.string().min(2, t("certificate.errors.required")).max(50),
    prenume: z.string().min(2, t("certificate.errors.required")).max(50),
    cnp: z.string().regex(/^\d{13}$/, t("certificate.errors.cnpInvalid")),
    cetatenie: z.string().min(2, t("certificate.errors.required")).max(50),
    // Step 2: Employer Info
    employer_name: z.string().min(2, t("certificate.errors.required")).max(100),
    employer_address: z
      .string()
      .min(5, t("certificate.errors.required"))
      .max(200),
    // Step 3: Contact
    email: z.string().email(t("certificate.errors.emailInvalid")),
    phone: z.string().optional(),
  });

const steps = [
  { id: 1, icon: User, key: "step1" },
  { id: 2, icon: Briefcase, key: "step2" },
  { id: 3, icon: Mail, key: "step3" },
  { id: 4, icon: FileUp, key: "step4" },
];

const AdeverintaPage = () => {
  const { t, isRTL, lang } = useLanguage();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [idCardFile, setIdCardFile] = useState(null);
  const [islamicCertFile, setIslamicCertFile] = useState(null);

  const schema = createSchema(t);

  const {
    register,
    handleSubmit,
    formState: { errors },
    trigger,
    watch,
  } = useForm({
    resolver: zodResolver(schema),
    mode: "onChange",
  });

  const validateStep = async () => {
    let fieldsToValidate = [];

    switch (currentStep) {
      case 1:
        fieldsToValidate = ["nume", "prenume", "cnp", "cetatenie"];
        break;
      case 2:
        fieldsToValidate = ["employer_name", "employer_address"];
        break;
      case 3:
        fieldsToValidate = ["email"];
        break;
      default:
        return true;
    }

    const isValid = await trigger(fieldsToValidate);
    return isValid;
  };

  const nextStep = async () => {
    const isValid = await validateStep();
    if (isValid && currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    try {
      // First submit the basic data to get an ID
      const initialSubmit = {
        ...data,
      };

      const submittedData = await adeverintaAPI.submit(initialSubmit);
      const adeverintaId = submittedData.id;

      // Then upload documents if present
      if (idCardFile || islamicCertFile) {
        const formData = new FormData();
        if (idCardFile) {
          formData.append("id_card", idCardFile);
        }
        if (islamicCertFile) {
          formData.append("islamic_cert", islamicCertFile);
        }

        try {
          const uploadApi = axios.create({
            baseURL: process.env.REACT_APP_BACKEND_URL,
          });

          await uploadApi.post(
            `/api/adeverinta/${adeverintaId}/upload-documents`,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            },
          );

          toast.success(
            lang === "en"
              ? "Documents uploaded successfully"
              : "Documente încărcate cu succes",
          );
        } catch (uploadError) {
          toast.warning(
            lang === "en"
              ? "Documents could not be uploaded, but your certificate request was submitted"
              : "Documentele nu au putut fi încărcate, dar cererea dvs. a fost trimisă",
          );
        }
      }

      setIsSuccess(true);
      toast.success(t("certificate.success"));
    } catch (error) {
      const message = error.response?.data?.detail || "An error occurred";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <Layout>
        <section className="py-12 md:py-20 bg-islamic-pearl">
          <div className="max-w-2xl mx-auto px-4">
            <Card className="border-stone-200 shadow-lg">
              <CardContent className="p-8 md:p-12 text-center">
                <div className="w-20 h-20 rounded-full bg-islamic-teal/10 flex items-center justify-center mx-auto mb-6">
                  <Check className="w-10 h-10 text-islamic-teal" />
                </div>
                <h1 className="font-serif text-2xl md:text-3xl font-bold text-stone-800 mb-4">
                  {t("certificate.success")}
                </h1>
                <p className="text-stone-600 mb-8">
                  {t("certificate.successMsg")}
                </p>
                <Button
                  onClick={() => (window.location.href = "/")}
                  className="bg-islamic-teal hover:bg-islamic-teal-light"
                  data-testid="back-home-btn">
                  {t("nav.home")}
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Header */}
      <section className="bg-islamic-teal text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-serif text-3xl md:text-4xl font-bold mb-2">
            {t("certificate.title")}
          </h1>
          <p className="text-white/70">{t("certificate.subtitle")}</p>
        </div>
      </section>

      {false && (
      <section className="py-8 md:py-12 bg-islamic-pearl">
        <div className="max-w-2xl mx-auto px-4">
          {/* Stepper */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              {steps.map((step, index) => {
                const Icon = step.icon;
                const isActive = currentStep === step.id;
                const isCompleted = currentStep > step.id;

                return (
                  <React.Fragment key={step.id}>
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-colors ${
                          isCompleted
                            ? "bg-islamic-gold text-white"
                            : isActive
                            ? "bg-islamic-teal text-white"
                            : "bg-stone-200 text-stone-500"
                        }`}
                        data-testid={`step-indicator-${step.id}`}>
                        {isCompleted ? (
                          <Check className="w-5 h-5" />
                        ) : (
                          <Icon className="w-5 h-5" />
                        )}
                      </div>
                      <span
                        className={`text-xs mt-2 hidden sm:block ${
                          isActive
                            ? "text-islamic-teal font-medium"
                            : "text-stone-500"
                        }`}>
                        {t(`certificate.${step.key}`)}
                      </span>
                    </div>
                    {index < steps.length - 1 && (
                      <div
                        className={`flex-1 h-1 mx-2 rounded ${
                          currentStep > step.id
                            ? "bg-islamic-gold"
                            : "bg-stone-200"
                        }`}></div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
            <Progress value={(currentStep / 4) * 100} className="h-2" />
          </div>

          {/* Form Card */}
          <Card className="border-stone-200 shadow-lg">
            <CardContent className="p-6 md:p-8">
              <form onSubmit={handleSubmit(onSubmit)}>
                {/* Step 1: Personal Info */}
                {currentStep === 1 && (
                  <div
                    className="space-y-6 animate-fade-in-up"
                    data-testid="step-1-form">
                    <h2 className="font-serif text-xl font-semibold text-stone-800 mb-4">
                      {t("certificate.step1")}
                    </h2>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="nume">
                          {t("certificate.lastName")} *
                        </Label>
                        <Input
                          id="nume"
                          {...register("nume")}
                          className={errors.nume ? "border-red-500" : ""}
                          data-testid="input-nume"
                        />
                        {errors.nume && (
                          <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {errors.nume.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="prenume">
                          {t("certificate.firstName")} *
                        </Label>
                        <Input
                          id="prenume"
                          {...register("prenume")}
                          className={errors.prenume ? "border-red-500" : ""}
                          data-testid="input-prenume"
                        />
                        {errors.prenume && (
                          <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {errors.prenume.message}
                          </p>
                        )}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="cnp">{t("certificate.cnp")} *</Label>
                      <Input
                        id="cnp"
                        {...register("cnp")}
                        maxLength={13}
                        placeholder="1234567890123"
                        className={errors.cnp ? "border-red-500" : ""}
                        data-testid="input-cnp"
                      />
                      {errors.cnp && (
                        <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {errors.cnp.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="cetatenie">
                        {t("certificate.nationality")} *
                      </Label>
                      <Input
                        id="cetatenie"
                        {...register("cetatenie")}
                        className={errors.cetatenie ? "border-red-500" : ""}
                        data-testid="input-cetatenie"
                      />
                      {errors.cetatenie && (
                        <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {errors.cetatenie.message}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Step 2: Employer Info */}
                {currentStep === 2 && (
                  <div
                    className="space-y-6 animate-fade-in-up"
                    data-testid="step-2-form">
                    <h2 className="font-serif text-xl font-semibold text-stone-800 mb-4">
                      {t("certificate.step2")}
                    </h2>

                    <div>
                      <Label htmlFor="employer_name">
                        {t("certificate.employerName")} *
                      </Label>
                      <Input
                        id="employer_name"
                        {...register("employer_name")}
                        className={errors.employer_name ? "border-red-500" : ""}
                        data-testid="input-employer-name"
                      />
                      {errors.employer_name && (
                        <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {errors.employer_name.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="employer_address">
                        {t("certificate.employerAddress")} *
                      </Label>
                      <Input
                        id="employer_address"
                        {...register("employer_address")}
                        className={
                          errors.employer_address ? "border-red-500" : ""
                        }
                        data-testid="input-employer-address"
                      />
                      {errors.employer_address && (
                        <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {errors.employer_address.message}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Step 3: Contact */}
                {currentStep === 3 && (
                  <div
                    className="space-y-6 animate-fade-in-up"
                    data-testid="step-3-form">
                    <h2 className="font-serif text-xl font-semibold text-stone-800 mb-4">
                      {t("certificate.step3")}
                    </h2>

                    <div>
                      <Label htmlFor="email">{t("certificate.email")} *</Label>
                      <Input
                        id="email"
                        type="email"
                        {...register("email")}
                        className={errors.email ? "border-red-500" : ""}
                        data-testid="input-email"
                      />
                      {errors.email && (
                        <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {errors.email.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="phone">{t("certificate.phone")}</Label>
                      <Input
                        id="phone"
                        type="tel"
                        {...register("phone")}
                        data-testid="input-phone"
                      />
                    </div>
                  </div>
                )}

                {/* Step 4: Documents */}
                {currentStep === 4 && (
                  <div
                    className="space-y-6 animate-fade-in-up"
                    data-testid="step-4-form">
                    <h2 className="font-serif text-xl font-semibold text-stone-800 mb-4">
                      {t("certificate.step4")}
                    </h2>

                    <div>
                      <Label>{t("certificate.uploadId")} *</Label>
                      <div className="mt-2">
                        <label
                          className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                            idCardFile
                              ? "border-islamic-teal bg-islamic-teal/5"
                              : "border-stone-300 hover:border-islamic-teal hover:bg-stone-50"
                          }`}
                          data-testid="upload-id-card">
                          <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            {idCardFile ? (
                              <>
                                <Check className="w-8 h-8 text-islamic-teal mb-2" />
                                <p className="text-sm text-islamic-teal">
                                  {idCardFile.name}
                                </p>
                              </>
                            ) : (
                              <>
                                <Upload className="w-8 h-8 text-stone-400 mb-2" />
                                <p className="text-sm text-stone-500">
                                  Click to upload
                                </p>
                                <p className="text-xs text-stone-400">
                                  Max 5MB
                                </p>
                              </>
                            )}
                          </div>
                          <input
                            type="file"
                            className="hidden"
                            accept="image/*,.pdf"
                            onChange={(e) => setIdCardFile(e.target.files[0])}
                          />
                        </label>
                      </div>
                    </div>

                    <div>
                      <Label>{t("certificate.uploadCert")}</Label>
                      <div className="mt-2">
                        <label
                          className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                            islamicCertFile
                              ? "border-islamic-gold bg-islamic-gold/5"
                              : "border-stone-300 hover:border-islamic-gold hover:bg-stone-50"
                          }`}
                          data-testid="upload-islamic-cert">
                          <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            {islamicCertFile ? (
                              <>
                                <Check className="w-8 h-8 text-islamic-gold mb-2" />
                                <p className="text-sm text-islamic-gold">
                                  {islamicCertFile.name}
                                </p>
                              </>
                            ) : (
                              <>
                                <Upload className="w-8 h-8 text-stone-400 mb-2" />
                                <p className="text-sm text-stone-500">
                                  Click to upload (optional)
                                </p>
                                <p className="text-xs text-stone-400">
                                  Max 5MB
                                </p>
                              </>
                            )}
                          </div>
                          <input
                            type="file"
                            className="hidden"
                            accept="image/*,.pdf"
                            onChange={(e) =>
                              setIslamicCertFile(e.target.files[0])
                            }
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {/* Navigation Buttons */}
                <div
                  className={`flex ${
                    currentStep === 1 ? "justify-end" : "justify-between"
                  } mt-8 pt-6 border-t border-stone-200`}>
                  {currentStep > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={prevStep}
                      className="gap-2"
                      data-testid="prev-step-btn">
                      <ArrowLeft
                        className={`w-4 h-4 ${isRTL ? "rotate-180" : ""}`}
                      />
                      {t("certificate.prev")}
                    </Button>
                  )}

                  {currentStep < 4 ? (
                    <Button
                      type="button"
                      onClick={nextStep}
                      className="bg-islamic-teal hover:bg-islamic-teal-light gap-2"
                      data-testid="next-step-btn">
                      {t("certificate.next")}
                      <ArrowRight
                        className={`w-4 h-4 ${isRTL ? "rotate-180" : ""}`}
                      />
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      disabled={isSubmitting || !idCardFile}
                      className="bg-islamic-teal hover:bg-islamic-teal-light gap-2"
                      data-testid="submit-btn">
                      {isSubmitting
                        ? t("common.loading")
                        : t("certificate.submit")}
                    </Button>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
      )}

      <section className="py-8 md:py-12 bg-islamic-pearl">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <a
            href="https://docs.google.com/forms/d/e/1FAIpQLSdiLXFSFKbIKKQT_Sdl3-JNoOAb7vAHPx888GrQerldSvTljw/viewform?usp=header"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-islamic-teal hover:bg-islamic-teal-light text-white font-semibold py-3 px-8 rounded-lg transition-colors text-lg">
            {t("certificate.applyForm")}
          </a>
        </div>
      </section>
    </Layout>
  );
};

export default AdeverintaPage;
